import { useState } from 'react';
import axios from 'axios';
import { Search, Download, Loader2, AlertCircle } from 'lucide-react';
import { PageTitle } from '../../../../_metronic/layout/core'
import * as XLSX from 'xlsx';

function InfrapayHistoricoVouchers() {
  const [cnpj, setCnpj] = useState('');
  const [voucherData, setVoucherData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const formatCNPJ = (value:any) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
    if (numbers.length <= 8) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`;
    if (numbers.length <= 12) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`;
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
  };

  const handleCNPJChange = (e:any) => {
    const formatted = formatCNPJ(e.target.value);
    setCnpj(formatted);
  };

  const isValidCNPJ = () => {
    const cleanCNPJ = cnpj.replace(/\D/g, '');
    return cleanCNPJ.length === 14;
  };

  const handleSearch = async () => {
    const cleanCNPJ = cnpj.replace(/\D/g, '');

    if (cleanCNPJ.length !== 14) {
      setError('CNPJ deve conter 14 dígitos');
      return;
    }

    setLoading(true);
    setError('');
    setVoucherData(null);

    try {
      const response = await axios.get(`/infrapay/historico_vouchers`,
        { params: { cnpj: cleanCNPJ } }
      );

      if (response.data.success && response.data.data.success) {
        setVoucherData(response.data);
      } else {
        setError('Nenhum dado encontrado para este CNPJ');
      }
    } catch (err) {
      setError('Erro ao buscar dados. Tente novamente.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e:any) => {
    if (e.key === 'Enter' && isValidCNPJ() && !loading) {
      handleSearch();
    }
  };

  const handleExport = () => {
  if (!voucherData?.data) return;

  const { columns, data } = voucherData.data;

  // Filtra as colunas exportadas removendo "cnpj" (case-insensitive)
  const exportColumns = columns.filter((c: string) => !hiddenColumnPredicate(String(c)));

  // Monta os objetos linha -> apenas com as colunas permitidas
  const exportData = data.map((row: any[]) => {
    const obj: any = {};
    columns.forEach((col: string, i: number) => {
      if (!hiddenColumnPredicate(col)) {
        obj[col] = row[i]; // se preferir cabeçalho "bonito", use col.replace(/_/g, ' ')
      }
    });
    return obj;
  });

  const worksheet = XLSX.utils.json_to_sheet(exportData, { header: exportColumns });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Vouchers');

  XLSX.writeFile(workbook, `historico_vouchers_${cnpj.replace(/\D/g, '')}.xlsx`);
};

  const formatValue = (value:any) => {
    if (typeof value === 'number' || !isNaN(Number(value))) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(Number(value));
    }
    return value;
  };

  const formatDate = (dateString:any) => {
    if (typeof dateString === 'string' && dateString.includes('T')) {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return dateString;
  };

  const getStatusColor = (status:any) => {
    const statusStr = String(status).toLowerCase();
    if (statusStr === 'válido') return 'text-green-700 bg-green-100 border-green-200';
    if (statusStr === 'expirado') return 'text-red-700 bg-red-100 border-red-200';
    if (statusStr === 'usado') return 'text-gray-700 bg-gray-100 border-gray-200';
    return 'text-blue-700 bg-blue-100 border-blue-200';
  };

  // ---------- NOVO: utilitários para esconder a coluna "cnpj" na TABELA ----------
  const hiddenColumnPredicate = (name: string) => name?.toLowerCase() === 'cnpj';

  const hiddenIndices = new Set<number>(
    voucherData?.data?.columns
      ?.map((c: string, i: number) => (hiddenColumnPredicate(c) ? i : -1))
      ?.filter((i: number) => i >= 0) ?? []
  );

  const visibleColumns = voucherData?.data?.columns
    ?.filter((_: string, i: number) => !hiddenIndices.has(i)) ?? [];
  // ------------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <PageTitle>Histórico de Vouchers Infrapay</PageTitle>
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        {/* Header Section */}
        <header className="mb-12 text-center">
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Consulte o histórico completo de vouchers através do CNPJ da empresa
          </p>
        </header>

        {/* Search Card */}
        <div className="bg-white rounded-2xl shadow-lg shadow-gray-200/50 p-8 mb-8 border border-gray-100 transition-all duration-300 hover:shadow-xl">
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="flex-1">
              <label htmlFor="cnpj" className="block text-sm font-semibold text-gray-700 mb-3">
                CNPJ da Empresa
              </label>
              <input
                id="cnpj"
                type="text"
                value={cnpj}
                onChange={handleCNPJChange}
                onKeyPress={handleKeyPress}
                placeholder="00.000.000/0000-00"
                maxLength={18}
                className="w-full px-5 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200 text-base font-medium outline-none"
                aria-label="Campo de busca por CNPJ"
              />
              <p className="mt-2 text-xs text-gray-500">
                Pressione Enter para buscar
              </p>
            </div>
            <div className="flex items-center">
              <button
                onClick={handleSearch}
                disabled={loading || !isValidCNPJ()}
                className="w-full sm:w-auto px-8 py-3.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-blue-600 transition-all duration-200 flex items-center justify-center gap-3 font-semibold text-base shadow-lg shadow-blue-600/20 hover:shadow-xl hover:shadow-blue-600/30 hover:-translate-y-0.5"
                aria-label="Buscar histórico de vouchers"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Buscando...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    Buscar
                  </>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-700 flex items-start gap-3 animate-in fade-in duration-300">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <span className="font-medium">{error}</span>
            </div>
          )}
        </div>

        {/* Results Section */}
        {voucherData?.data && (
          <div className="bg-white rounded-2xl shadow-lg shadow-gray-200/50 overflow-hidden border border-gray-100 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="px-8 py-6 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gradient-to-r from-gray-50 to-white">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">
                  Resultados da Busca
                </h2>
                <p className="text-sm text-gray-600 font-medium">
                  {voucherData.data.data.length} voucher(s) encontrado(s) para o CNPJ {cnpj}
                </p>
              </div>
              <button
                onClick={handleExport}
                className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 focus:ring-4 focus:ring-green-200 transition-all duration-200 flex items-center gap-2.5 font-semibold shadow-lg shadow-green-600/20 hover:shadow-xl hover:shadow-green-600/30 hover:-translate-y-0.5"
                aria-label="Exportar dados para Excel"
              >
                <Download className="w-5 h-5" />
                Exportar Excel
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    {visibleColumns.map((column:any, index:any) => (
                      <th
                        key={index}
                        className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap"
                      >
                        {column.replace(/_/g, ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {voucherData.data.data.map((row:any, rowIndex:any) => (
                    <tr
                      key={rowIndex}
                      className="hover:bg-blue-50/50 transition-colors duration-150"
                    >
                      {row
                        .filter((_: any, i: number) => !hiddenIndices.has(i))
                        .map((cell:any, cellIndexVisible:any, arr:any[]) => {
                          // Precisamos do índice original para saber o nome da coluna.
                          // Para isso, recuperamos a posição original percorrendo columns e pulando os ocultos.
                          let originalIndex = -1;
                          for (let i = 0, seen = 0; i < voucherData.data.columns.length; i++) {
                            if (!hiddenIndices.has(i)) {
                              if (seen === cellIndexVisible) {
                                originalIndex = i;
                                break;
                              }
                              seen++;
                            }
                          }
                          const columnName = voucherData.data.columns[originalIndex];
                          let content = cell;

                          if (columnName === 'valor') {
                            content = <span className="font-semibold text-gray-900">{formatValue(cell)}</span>;
                          } else if (columnName === 'criado_em' || columnName === 'expira_em') {
                            content = <span className="text-gray-700">{formatDate(cell)}</span>;
                          } else if (columnName === 'status') {
                            content = (
                              <span className={`inline-flex px-3 py-1.5 rounded-lg text-xs font-bold border ${getStatusColor(cell)}`}>
                                {cell}
                              </span>
                            );
                          }

                          return (
                            <td
                              key={cellIndexVisible}
                              className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                            >
                              {content}
                            </td>
                          );
                        })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!voucherData && !loading && !error && (
          <div className="bg-white rounded-2xl shadow-lg shadow-gray-200/50 p-16 text-center border border-gray-100">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
              <Search className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              Nenhuma busca realizada
            </h3>
            <p className="text-gray-600 max-w-md mx-auto leading-relaxed">
              Digite um CNPJ válido no campo acima e clique em buscar para visualizar o histórico de vouchers
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default InfrapayHistoricoVouchers;
